import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Tooltip, Legend, Filler,
} from "chart.js";
import Layout from "../../components/Layout";
import { api } from "./api";
import "./AdminDashboard.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const RANGES = [7, 30, 90];
const fmtUsd = (n) => `$${(n || 0).toFixed(2)}`;
const fmtNum = (n) => (n || 0).toLocaleString();
const dayTokens = (d) =>
  d.input_tokens + d.output_tokens + d.cache_read_tokens + d.cache_creation_tokens;

// Resolve a --ca-* design token to its current (theme-adapted) value so the
// canvas chart matches the surrounding CSS. Canvas can't read CSS vars directly.
const cssVar = (name, fallback) => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

export default function ClaudeAgentAdminDashboard() {
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [metric, setMetric] = useState("tokens"); // "tokens" | "usd"
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true); setError(null);
    api.adminUsage(days)
      .then((d) => { if (live) setUsage(d); })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [days]);

  const byDay = usage?.by_day || [];
  const byUser = usage?.by_user || [];
  const totals = usage?.totals || {};

  const accent = cssVar("--ca-accent", "#3469ff");
  const tickColor = cssVar("--ca-text-muted", "#9ca3af");
  const gridColor = cssVar("--ca-border", "#e5e7eb");

  const chartData = {
    labels: byDay.map((d) => d.date),
    datasets: [{
      label: metric === "usd" ? "USD" : "Tokens",
      data: byDay.map((d) => (metric === "usd" ? d.cost_usd : dayTokens(d))),
      borderColor: accent,
      backgroundColor: "rgba(52,105,255,0.12)",
      pointBackgroundColor: accent,
      fill: true, tension: 0.25, pointRadius: 2,
    }],
  };
  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: tickColor, maxRotation: 0, autoSkip: true }, grid: { color: gridColor } },
      y: {
        ticks: {
          color: tickColor,
          callback: (v) => metric === "usd"
            ? `$${v}`
            : Intl.NumberFormat("en", { notation: "compact" }).format(v),
        },
        grid: { color: gridColor },
      },
    },
  };

  return (
    <Layout>
      <div className="cad-page">
        <div className="cad-inner">
          <header className="cad-head">
          <button className="ca-btn ca-btn--ghost ca-btn--sm" onClick={() => navigate(-1)}>← Back</button>
          <h1>Claude Agent — Usage Dashboard</h1>
        </header>

        {error && <div className="cad-error">{error}</div>}

        <div className="cad-controls">
          <div className="cad-seg">
            {RANGES.map((r) => (
              <button key={r} className={days === r ? "active" : ""}
                      onClick={() => setDays(r)}>{r}d</button>
            ))}
          </div>
          <div className="cad-seg">
            <button className={metric === "tokens" ? "active" : ""}
                    onClick={() => setMetric("tokens")}>Tokens</button>
            <button className={metric === "usd" ? "active" : ""}
                    onClick={() => setMetric("usd")}>USD</button>
          </div>
        </div>

        <section className="cad-cards">
          <Card label="Total tokens" value={fmtNum(totals.tokens)} />
          <Card label="Total cost" value={fmtUsd(totals.cost_usd)} />
          <Card label="Messages" value={fmtNum(totals.message_count)} />
          <Card label="Tool calls" value={fmtNum(totals.tool_call_count)} />
          <Card label="Active users" value={fmtNum(totals.active_users)} />
        </section>

        <section className="cad-chart">
          {loading ? <div className="cad-chart-empty">Loading…</div>
            : byDay.length === 0 ? <div className="cad-chart-empty">No usage in this range.</div>
            : <Line data={chartData} options={chartOptions} />}
        </section>

        <section className="cad-users">
          <h2>By user</h2>
          <div className="cad-table-wrap">
            <table>
              <thead>
                <tr><th>User</th><th>Messages</th><th>In</th><th>Out</th>
                    <th>Tool calls</th><th>Cost</th><th>Last active</th></tr>
              </thead>
              <tbody>
                {byUser.map((u) => (
                  <tr key={u.user_email}
                      className={selectedUser === u.user_email ? "selected" : ""}
                      onClick={() => setSelectedUser(u.user_email)}>
                    <td>{u.user_email}</td>
                    <td>{fmtNum(u.message_count)}</td>
                    <td>{fmtNum(u.input_tokens)}</td>
                    <td>{fmtNum(u.output_tokens)}</td>
                    <td>{fmtNum(u.tool_call_count)}</td>
                    <td>{fmtUsd(u.cost_usd)}</td>
                    <td>{u.last_active}</td>
                  </tr>
                ))}
                {byUser.length === 0 && !loading && (
                  <tr><td colSpan={7} className="cad-empty">No users yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

          {selectedUser && (
            <MessagesPanel userEmail={selectedUser} onClose={() => setSelectedUser(null)} />
          )}
        </div>
      </div>
    </Layout>
  );
}

function Card({ label, value }) {
  return (
    <div className="cad-card">
      <div className="cad-card-value">{value}</div>
      <div className="cad-card-label">{label}</div>
    </div>
  );
}

// This month's spend cap for one user. The override lives on the user's month
// doc, so it lapses on its own when the next UTC month starts.
function SpendLimit({ userEmail }) {
  const [info, setInfo] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const apply = (d) => { setInfo(d); setDraft(d.cap_usd == null ? "" : String(d.cap_usd)); };

  useEffect(() => {
    setInfo(null); setError(null);
    api.adminGetSpendLimit(userEmail).then(apply).catch((e) => setError(e.message));
  }, [userEmail]);

  const save = (capUsd) => {
    setSaving(true); setError(null);
    api.adminSetSpendLimit(userEmail, capUsd).then(apply)
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const n = Number(draft);
    if (draft.trim() === "" || !Number.isFinite(n) || n < 0) {
      setError("Enter a dollar amount of 0 or more (0 = unlimited).");
      return;
    }
    save(n);
  };

  if (!info) return error ? <div className="cad-error">{error}</div> : null;
  const effective = info.cap_usd ?? info.default_cap_usd;
  return (
    <form className="cad-spend" onSubmit={onSubmit}>
      <div className="cad-spend-summary">
        This month: <strong>${info.spent_usd.toFixed(2)}</strong> of{" "}
        <strong>{effective ? `$${effective.toFixed(2)}` : "unlimited"}</strong>
        {info.cap_usd == null ? " (default)" : " (custom)"}
        {info.is_admin && " · admin, exempt from the cap"}
      </div>
      <label className="cad-spend-edit">
        Limit this month ($)
        <input className="ca-input" type="number" min="0" step="1"
               placeholder={String(info.default_cap_usd)}
               value={draft} onChange={(e) => setDraft(e.target.value)} />
      </label>
      <button className="ca-btn ca-btn--sm" type="submit" disabled={saving}>Save</button>
      {info.cap_usd != null && (
        <button className="ca-btn ca-btn--sm ca-btn--ghost" type="button"
                disabled={saving} onClick={() => save(null)}>Reset to default</button>
      )}
      {error && <div className="cad-error">{error}</div>}
    </form>
  );
}

function MessagesPanel({ userEmail, onClose }) {
  const [q, setQ] = useState("");
  const [messages, setMessages] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback((reset) => {
    setLoading(true); setError(null);
    api.adminUserMessages(userEmail, { q, before: reset ? null : nextBefore })
      .then((d) => {
        setMessages((prev) => (reset ? d.messages : [...prev, ...d.messages]));
        setNextBefore(d.next_before);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [userEmail, q, nextBefore]);

  // Reset + reload when the user or (debounced) query changes.
  useEffect(() => {
    const t = setTimeout(() => load(true), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEmail, q]);

  return (
    <section className="cad-messages">
      <div className="cad-messages-head">
        <h2>Messages — {userEmail}</h2>
        <button className="ca-btn ca-btn--icon" onClick={onClose} aria-label="Close">×</button>
      </div>
      <SpendLimit userEmail={userEmail} />
      <input className="ca-input cad-search" placeholder="Search messages…"
             value={q} onChange={(e) => setQ(e.target.value)} />
      {error && <div className="cad-error">{error}</div>}
      <ul className="cad-msg-list">
        {messages.map((m, i) => (
          <li key={i}>
            <div className="cad-msg-text">{m.text}</div>
            <div className="cad-msg-meta">
              {m.timestamp ? new Date(m.timestamp).toLocaleString() : ""} · {m.agent_name}
              {m.thread_title ? ` · ${m.thread_title}` : ""}
            </div>
          </li>
        ))}
        {messages.length === 0 && !loading && <li className="cad-empty">No messages.</li>}
      </ul>
      {nextBefore && (
        <button className="ca-btn ca-btn--sm cad-more" onClick={() => load(false)} disabled={loading}>
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </section>
  );
}
